import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle, Phone, Mail, Loader2, BookOpen, ShieldCheck, TrendingUp, Award, Star, AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { SubstackHeaderButton, SubstackSlimFooter } from "@/components/SubstackLink";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BOOK_IMAGE = "https://customer-assets.emergentagent.com/job_ridgemont-leads/artifacts/x50ge65l_IMG_2743.webp";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.7 }, viewport: { once: true } };

const RidgemontLogo = ({ className = "" }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M50 12 L88 82 L12 82 Z" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
    <path d="M28 62 L72 62" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const FreeBookPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '',
    address_line1: '', address_line2: '', city: '', state: '', zip_code: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Load Taboola + GTM pixels (same as Premium Bonus page)
  useEffect(() => {
    window._tfa = window._tfa || [];
    window._tfa.push({ notify: 'event', name: 'page_view', id: 2036933 });
    if (!document.getElementById('tb_tfa_script')) {
      const s = document.createElement('script');
      s.async = 1;
      s.src = '//cdn.taboola.com/libtrc/unip/2036933/tfa.js';
      s.id = 'tb_tfa_script';
      const first = document.getElementsByTagName('script')[0];
      first.parentNode.insertBefore(s, first);
    }

    if (!document.getElementById('gtm_script')) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const g = document.createElement('script');
      g.async = true;
      g.id = 'gtm_script';
      g.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-PDRR56MH';
      const first = document.getElementsByTagName('script')[0];
      first.parentNode.insertBefore(g, first);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const formatPhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return `+${digits}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Required";
    if (!formData.email.trim()) newErrors.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Invalid email";
    if (!formData.phone.trim()) newErrors.phone = "Required";
    if (!formData.address_line1.trim()) newErrors.address_line1 = "Required";
    if (!formData.city.trim()) newErrors.city = "Required";
    if (!formData.state) newErrors.state = "Required";
    if (!formData.zip_code.trim()) newErrors.zip_code = "Required";
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/leads/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, phone: formatPhone(formData.phone) })
      });
      if (!res.ok) throw new Error('Lead creation failed');
      // GTM conversion event
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'free_book_lead', source: 'free_book_landing' });
      }
      // Taboola conversion
      if (window._tfa) {
        window._tfa.push({ notify: 'event', name: 'lead', id: 2036933 });
      }
      setSubmitted(true);
      // Scroll user back to top so they see the confirmation
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setErrors({ submit: 'Something went wrong. Please try again or email us at hello@ridgemontfinancial.com.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white" data-testid="free-book-page">
      {/* GTM noscript */}
      <noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-PDRR56MH" height="0" width="0" style={{ display: 'none', visibility: 'hidden' }} title="gtm"></iframe></noscript>

      {/* Slim top nav */}
      <header className="bg-white border-b border-[#E7E5E4]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="logo-home">
            <RidgemontLogo className="w-8 h-8 text-[#0A192F]" />
            <div className="flex flex-col leading-none">
              <span className="text-xl font-light tracking-tight text-[#0A192F]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ridgemont</span>
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#C5A059] mt-0.5">Financial</span>
            </div>
          </a>
          <a href="mailto:hello@ridgemontfinancial.com" className="flex items-center gap-2 text-[#0A192F] hover:text-[#C5A059] transition-colors text-sm font-medium" data-testid="header-email">
            <Mail className="w-4 h-4" />
            <span className="hidden sm:inline">hello@ridgemontfinancial.com</span>
          </a>
          <SubstackHeaderButton />
        </div>
      </header>

      {/* HERO + FORM */}
      <section className="relative bg-gradient-to-br from-[#0A192F] via-[#0A192F] to-[#1B4D3E] py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#C5A059] rounded-full blur-[180px]" />
        </div>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
            {/* LEFT: Book + headline */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-5" data-testid="hero-eyebrow">
                Free Book &middot; We Cover Shipping
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-[1.05] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                You've saved well.
                <span className="block text-[#C5A059]">How much will you actually keep?</span>
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-8 max-w-xl">
                Most pre-retirees discover the painful answer too late: <span className="text-white font-medium">taxes are the single biggest threat to retirement income.</span>
                {' '}<em>Tax-Free Retirement</em> by Patrick Kelly shows you exactly how to fix that &mdash; in plain English. We'll mail you a physical copy at no cost.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center mb-10">
                <motion.img
                  src={BOOK_IMAGE}
                  alt="Tax-Free Retirement by Patrick Kelly"
                  className="w-40 md:w-56 drop-shadow-2xl"
                  initial={{ opacity: 0, scale: 0.92, rotate: -3 }}
                  animate={{ opacity: 1, scale: 1, rotate: -3 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  data-testid="hero-book-image"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map(n => (
                      <Star key={n} className="w-4 h-4 fill-[#C5A059] text-[#C5A059]" />
                    ))}
                    <span className="text-white/60 text-xs ml-2">National Bestseller</span>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed">
                    <em>"One of the clearest, most practical guides to building income you actually keep in retirement."</em>
                  </p>
                </div>
              </div>

              {/* Inline trust badges */}
              <div className="grid grid-cols-3 gap-4 max-w-lg">
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-light text-[#C5A059]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>500k+</p>
                  <p className="text-white/50 text-[11px] leading-tight mt-1">Copies in print</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-light text-[#C5A059]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>$0</p>
                  <p className="text-white/50 text-[11px] leading-tight mt-1">Your cost (we ship free)</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-light text-[#C5A059]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>5-7d</p>
                  <p className="text-white/50 text-[11px] leading-tight mt-1">USPS delivery</p>
                </div>
              </div>
            </motion.div>

            {/* RIGHT: Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white p-8 md:p-10 shadow-2xl"
              data-testid="hero-form-card"
            >
              {submitted ? (
                <div className="text-center py-6" data-testid="book-success-state">
                  <div className="w-16 h-16 bg-[#1B4D3E] flex items-center justify-center mx-auto mb-5">
                    <Phone className="w-9 h-9 text-[#C5A059]" />
                  </div>
                  <h2 className="text-3xl font-light text-[#0A192F] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Got it. We'll call you to confirm.
                  </h2>
                  <p className="text-[#57534E] leading-relaxed mb-4">
                    A Ridgemont team member will give you a quick call within <span className="font-medium text-[#0A192F]">1&ndash;2 business days</span> to confirm your mailing address and answer any questions.
                    Once verified, your copy of <em>Tax-Free Retirement</em> ships within 24 hours.
                  </p>
                  <div className="bg-[#FAFAF9] border border-[#E7E5E4] p-4 mb-5 text-left">
                    <p className="text-xs uppercase tracking-wider text-[#C5A059] font-semibold mb-2">Shipping to</p>
                    <p className="text-[#0A192F] text-sm">{formData.name}</p>
                    <p className="text-[#0A192F] text-sm">
                      {formData.address_line1}{formData.address_line2 ? `, ${formData.address_line2}` : ''}<br />
                      {formData.city}, {formData.state} {formData.zip_code}
                    </p>
                    <p className="text-[#57534E] text-xs mt-2">We'll call: {formData.phone}</p>
                  </div>
                  <p className="text-[#57534E] text-xs">
                    Watch for a call from a <span className="font-medium">(480) area code</span>.
                    If you miss us, we'll leave a voicemail and follow up by email.
                  </p>
                  <a href="/" className="inline-block mt-6 text-[#C5A059] hover:underline text-sm font-medium">&larr; Back to Ridgemont Financial</a>
                </div>
              ) : (
                <div data-testid="book-form-state">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-5 h-5 text-[#C5A059]" />
                    <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059] font-semibold">Claim Your Free Copy</p>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-light text-[#0A192F] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Tell us where to mail it.
                  </h2>
                  <p className="text-sm text-[#57534E] mb-5">U.S. addresses only. We pay shipping. We'll call you to confirm the address before we ship.</p>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Input name="name" placeholder="Full Name *" value={formData.name} onChange={handleChange} className={errors.name ? 'border-red-500' : ''} data-testid="book-name-input" />
                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                      </div>
                      <div>
                        <Input name="email" type="email" placeholder="Email *" value={formData.email} onChange={handleChange} className={errors.email ? 'border-red-500' : ''} data-testid="book-email-input" />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                      </div>
                    </div>
                    <div>
                      <Input name="phone" type="tel" placeholder="Best phone to reach you *" value={formData.phone} onChange={handleChange} className={errors.phone ? 'border-red-500' : ''} data-testid="book-phone-input" />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>
                    <Input name="address_line1" placeholder="Mailing Address *" value={formData.address_line1} onChange={handleChange} className={errors.address_line1 ? 'border-red-500' : ''} data-testid="book-address1-input" />
                    {errors.address_line1 && <p className="text-red-500 text-xs">{errors.address_line1}</p>}
                    <Input name="address_line2" placeholder="Apt, Suite, Unit (optional)" value={formData.address_line2} onChange={handleChange} data-testid="book-address2-input" />
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Input name="city" placeholder="City *" value={formData.city} onChange={handleChange} className={errors.city ? 'border-red-500' : ''} data-testid="book-city-input" />
                        {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                      </div>
                      <div>
                        <Select value={formData.state} onValueChange={(val) => { setFormData(prev => ({ ...prev, state: val })); if (errors.state) setErrors(prev => ({ ...prev, state: '' })); }}>
                          <SelectTrigger className={errors.state ? 'border-red-500' : ''} data-testid="book-state-select">
                            <SelectValue placeholder="State *" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                      </div>
                      <div>
                        <Input name="zip_code" placeholder="Zip *" maxLength={10} value={formData.zip_code} onChange={handleChange} className={errors.zip_code ? 'border-red-500' : ''} data-testid="book-zip-input" />
                        {errors.zip_code && <p className="text-red-500 text-xs mt-1">{errors.zip_code}</p>}
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#0A192F] text-white hover:bg-[#0A192F]/90 py-6 text-base font-semibold"
                      data-testid="book-submit-btn"
                    >
                      {isSubmitting ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending...</>) : (<>Ship My Free Book</>)}
                    </Button>
                    {errors.submit && <p className="text-red-500 text-xs text-center">{errors.submit}</p>}
                    <div className="flex items-start gap-2 text-[11px] text-[#57534E]/80 text-left mt-2 leading-relaxed">
                      <Phone className="w-3.5 h-3.5 mt-0.5 text-[#C5A059] flex-shrink-0" />
                      <span>
                        A Ridgemont team member will call within 1&ndash;2 business days to confirm your address.
                        No sales pitch. No purchase required. We never sell your information.
                      </span>
                    </div>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS — Speaks directly to the marketing demographic */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-4">If This Sounds Like You</p>
            <h2 className="text-3xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              You did the right things.
              <span className="block">Now the rules are changing.</span>
            </h2>
            <p className="text-lg text-[#57534E] mt-4 max-w-2xl mx-auto">
              You followed the playbook &mdash; maxed the 401(k), funded the IRA, stayed disciplined.
              But the playbook didn't tell you what taxes would do to your retirement income on the way out.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { quote: "I don't want to lose a big chunk of my retirement to taxes." },
              { quote: "I don't actually know how much of my money is mine after taxes." },
              { quote: "I've saved well, but I still don't have a clear income plan." }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                viewport={{ once: true }}
                className="bg-[#FAFAF9] border-l-4 border-[#C5A059] p-7 md:p-8"
                data-testid={`pain-${i}`}
              >
                <AlertTriangle className="w-6 h-6 text-[#C5A059] mb-4" />
                <p className="text-[#0A192F] text-lg leading-relaxed italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  &ldquo;{item.quote}&rdquo;
                </p>
              </motion.div>
            ))}
          </div>

          <motion.p {...fadeUp} className="text-center text-[#57534E] mt-12 max-w-2xl mx-auto text-lg">
            If any of those hit close, this book was written for you.
          </motion.p>
        </div>
      </section>

      {/* WHAT YOU'LL LEARN */}
      <section className="py-20 md:py-28 bg-[#FAFAF9]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-4">Inside the Book</p>
            <h2 className="text-3xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              What you'll learn in <em>Tax-Free Retirement</em>
            </h2>
            <p className="text-lg text-[#57534E] mt-4 max-w-2xl mx-auto">
              The same strategies high-income earners and savvy retirees use to keep more of what they've built.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {[
              { icon: ShieldCheck, title: "The 9 Financial Landmines", desc: "How taxes, inflation, market downturns, and 6 other hidden risks quietly destroy retirement income — and the simple shifts that defuse each one." },
              { icon: TrendingUp, title: "Tax-Free Retirement Income", desc: "Why the right structure can turn distributions into income the IRS treats differently than a 401(k) or IRA withdrawal." },
              { icon: Award, title: "Multiplying Your Wealth", desc: "How leverage, tax efficiency, and protected growth can compound your dollars two or three-fold versus traditional plans." },
              { icon: BookOpen, title: "Leaving a Lasting Legacy", desc: "Structures that pass wealth to your kids and grandkids without handing the IRS a chunk on the way out." }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-[#E7E5E4] p-7 md:p-8 flex gap-4"
                data-testid={`learn-card-${i}`}
              >
                <div className="w-12 h-12 bg-[#0A192F] flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="text-xl font-medium text-[#0A192F] mb-2">{item.title}</h3>
                  <p className="text-[#57534E] leading-relaxed text-sm">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-4">Is This For You?</p>
            <h2 className="text-3xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Built for savers who'd rather plan than guess.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div {...fadeUp} className="bg-[#FAFAF9] border border-[#E7E5E4] p-7">
              <p className="text-xs tracking-[0.2em] uppercase text-[#1B4D3E] font-semibold mb-4">This book is for you if&hellip;</p>
              <ul className="space-y-3">
                {[
                  "You're 45-65, pre-retiree or recently retired",
                  "You've built real savings ($100K-$300K+ income, 401(k)/IRA/brokerage)",
                  "You're worried taxes are going up before you retire",
                  "You want safety, predictability, and control over your money",
                  "You'd rather read 1 book than sit through 5 sales pitches"
                ].map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#0A192F] text-sm">
                    <CheckCircle className="w-5 h-5 text-[#1B4D3E] flex-shrink-0 mt-0.5" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.15 }} className="bg-[#0A192F] p-7 text-white">
              <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">It's probably not for you if&hellip;</p>
              <ul className="space-y-3 text-white/80 text-sm">
                <li>&middot; You're looking for a get-rich-quick angle</li>
                <li>&middot; You think planning your retirement is your CPA's problem</li>
                <li>&middot; You want a free PDF &mdash; we mail a physical copy only</li>
                <li>&middot; You're not in the U.S. (we can't ship internationally)</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* GOALS / WHAT YOU GET OUT OF IT */}
      <section className="py-20 md:py-28 bg-[#1B4D3E] text-white">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-4">What You'll Walk Away With</p>
            <h2 className="text-3xl md:text-5xl font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Clarity. Control. And a real plan.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {[
              { title: "Tax-Efficient Retirement Income", desc: "Understand exactly which dollars to spend first, second, and last to keep your effective tax rate as low as legally possible." },
              { title: "Protection From What You Built", desc: "Know how to shield retirement assets from the two biggest threats facing your generation: tax-rate increases and market volatility." },
              { title: "More Certainty. More Peace of Mind.", desc: "Replace the &ldquo;am I going to be okay?&rdquo; question with a written income plan you can actually point to." },
              { title: "A Legacy, Passed Cleanly", desc: "Strategies for passing money to children and grandchildren without handing a chunk of it to the IRS on the way out." }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/5 border border-white/10 p-7"
                data-testid={`goal-${i}`}
              >
                <CheckCircle className="w-6 h-6 text-[#C5A059] mb-3" />
                <h3 className="text-xl font-medium text-white mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{item.title}</h3>
                <p className="text-white/70 leading-relaxed text-sm" dangerouslySetInnerHTML={{ __html: item.desc }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#FAFAF9]">
        <div className="max-w-[900px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-4">Common Questions</p>
            <h2 className="text-3xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Quick answers before you order.
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              { q: "Is the book really free?", a: "Yes. We cover the cost of the book and the USPS shipping. The only thing we ask is that you spend 2-3 minutes on a quick phone call so we can confirm the mailing address is correct before we ship." },
              { q: "Why do you call to confirm the address?", a: "Two reasons. First, we ship every copy at our expense, so confirming the address protects the book from getting lost or returned. Second, it gives us a chance to answer any quick questions you may have before reading. There is no sales pitch — your address is the only thing we need to confirm." },
              { q: "What if I miss the call?", a: "No worries — we'll leave a voicemail and follow up by email. You're also welcome to reply to us at hello@ridgemontfinancial.com." },
              { q: "How fast does the book arrive?", a: "Once we confirm your address, the book ships within 24 hours and arrives within 5-7 business days via USPS. Rural and PO Box addresses can take slightly longer." },
              { q: "Will I be pressured to buy something?", a: "No. The book is genuinely a no-cost educational resource. A Ridgemont advisor may follow up after you've had time to read it — only if you indicate you'd like to discuss your own situation." },
              { q: "Who is Patrick Kelly?", a: "Patrick Kelly is a national bestselling author and retirement strategist. Tax-Free Retirement has sold over 500,000 copies and is widely used by financial professionals as a client education resource." }
            ].map((item, i) => (
              <motion.details
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                viewport={{ once: true }}
                className="bg-white border border-[#E7E5E4] p-5 md:p-6 group"
                data-testid={`faq-${i}`}
              >
                <summary className="cursor-pointer font-medium text-[#0A192F] flex items-center justify-between list-none">
                  <span className="pr-4">{item.q}</span>
                  <span className="text-[#C5A059] text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-[#57534E] mt-3 leading-relaxed text-sm">{item.a}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-24 bg-[#0A192F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-15 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#C5A059] rounded-full blur-[180px]" />
        </div>
        <div className="max-w-[800px] mx-auto px-4 md:px-8 text-center relative z-10">
          <motion.div {...fadeUp}>
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold mb-5">Last Thing</p>
            <h2 className="text-3xl md:text-5xl font-light text-white tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              You scrolled this far.
              <span className="block text-[#C5A059]">Now claim the book.</span>
            </h2>
            <p className="text-white/70 mb-8 leading-relaxed">
              An hour of reading could save you years of tax exposure you didn't see coming.
            </p>
            <Button
              onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-10 py-7 text-lg font-semibold"
              data-testid="final-cta-btn"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              Claim My Free Copy
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Slim Footer */}
      <footer className="bg-[#0A192F] border-t border-white/10 py-8">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <p>&copy; {new Date().getFullYear()} Ridgemont Financial. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <a href="/privacy-policy" className="hover:text-[#C5A059] transition-colors">Privacy</a>
            <a href="/terms-of-service" className="hover:text-[#C5A059] transition-colors">Terms</a>
            <a href="mailto:hello@ridgemontfinancial.com" className="hover:text-[#C5A059] transition-colors flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" />hello@ridgemontfinancial.com
            </a>
            <SubstackSlimFooter />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FreeBookPage;
